import { Checkbox } from '@/components/ui/checkbox'
import { PlaceSelectorComponent } from '../../components/gmaps/place-selector.component'
import toast from 'react-hot-toast'
import { validateAddress } from '../../services/gmaps'
import { useEffect, useRef, useState } from 'react'
import { PharmacySelectorDialogComponent } from '../../components/dialog/pharmacy-selector.dialog'
import { ActionButton } from '@/components/ui/custom/action-button'
import { LoadingComponent } from '@/components/loading/loading.component'
import { PharmacyOrder, Prescription } from '@/dto/api/prescription.dto'
import { createBooking, getPrescriptionById } from '@/services/api.service'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { placeToAddressDetails } from '@/util/address.util'
import styled from 'styled-components'
import { trackEvent, trackPharmacyAvailability } from '@/services/analytics.service'

interface Props {
  prescriptionId: string
  prescription: Prescription
  disabled: boolean
}

function getAddressComponent (place: google.maps.places.PlaceResult | null, type: string): string | undefined {
  return place?.address_components?.find((component) => component.types.includes(type))?.long_name
}

const PrescriptionDetailsPageStyle = styled.section`
  .external-pharmacy {
    color: #b4290d;
}
`

export function PrescriptionDetailsPage ({ prescriptionId, prescription, disabled }: Props): JSX.Element {
  const navigate = useNavigate()
  const initialAddress = prescription.address != null
    ? {
        formatted_address: prescription.address.address,
        geometry: {
          location: {
            lat: () => prescription.address?.geometry?.coordinates[1],
            lng: () => prescription.address?.geometry?.coordinates[0]
          } as any
        }
      }
    : null
  const [pharmacies, setPharmacies] = useState<PharmacyOrder[]>(initialAddress != null ? prescription.pharmacies : [])
  const [selectedPharmacy, setSelectedPharmacy] = useState<PharmacyOrder | null>(initialAddress != null ? prescription.pharmacies.find((p) => p.selected) ?? null : null)
  const [changeAddressOpen, setChangeAddressOpen] = useState(initialAddress == null)
  const [changePharmacyOpen, setChangePharmacyOpen] = useState(false)
  const [checked, setChecked] = useState(false)
  const isInitialMount = useRef(true)
  const [address, setAddress] = useState<google.maps.places.PlaceResult | null>(initialAddress)
  const [submiting, setSubmiting] = useState(false)
  const [fetchingPharmacies, setFetchingPharmacies] = useState(false)

  const onPlaceSelect = (place: google.maps.places.PlaceResult): void => {
    const errors = validateAddress(place)

    if (errors.length > 0) {
      trackEvent('address_invalid', {
        errors_count: errors.length,
        error_message: errors.join(' | ')
      })

      for (const error of errors) {
        toast.error(error)
      }
      return
    }

    trackEvent('address_selected', {
      is_first_address: address == null,
      city: getAddressComponent(place, 'locality'),
      province: getAddressComponent(place, 'administrative_area_level_1'),
      postal_code: getAddressComponent(place, 'postal_code'),
      country: getAddressComponent(place, 'country')
    })

    setAddress(place)
    setChangeAddressOpen(false)
  }

  const onPharmacySelect = (pharmacy: PharmacyOrder | string): void => {
    const pharma = typeof pharmacy === 'string' ? pharmacies.find((p) => p.pharmacy.id === pharmacy) : pharmacy

    trackEvent('pharmacy_selected', {
      pharmacy_id: pharma?.pharmacy.id,
      is_external: pharma?.pharmacy.isExternal,
      position: pharma != null ? pharmacies.indexOf(pharma) + 1 : undefined,
      items_count: pharma?.items.length,
      source: 'selector_dialog'
    })

    setPharmacies(pharmacies.map((p) => {
      p.selected = p.pharmacy.id === (pharma?.pharmacy.id ?? pharmacy)
      return p
    }))
  }

  const onSubmit = async (): Promise<void> => {
    try {
      setSubmiting(true)

      if (selectedPharmacy == null) {
        throw new Error('No pharmacy selected')
      }

      trackEvent('booking_submit', {
        pharmacy_id: selectedPharmacy.pharmacy.id,
        is_external: selectedPharmacy.pharmacy.isExternal,
        items_count: selectedPharmacy.items.length,
        total: selectedPharmacy.total
      })

      await createBooking(prescriptionId, selectedPharmacy.pharmacy.id)

      trackEvent('booking_success', {
        pharmacy_id: selectedPharmacy.pharmacy.id,
        items_count: selectedPharmacy.items.length,
        total: selectedPharmacy.total,
        pharmacies_offered: pharmacies.length
      })

      navigate(`/${prescriptionId}/success`)
    } catch (error: any) {
      console.error(error)

      const message = error.response?.data?.message ?? 'Ha ocurrido un error al crear la reserva'

      trackEvent('booking_error', {
        pharmacy_id: selectedPharmacy?.pharmacy.id,
        error_message: message,
        error_status: error.response?.status
      })

      toast.error(message)
    } finally {
      setSubmiting(false)
    }
  }

  const handleOnSubmit = (): void => {
    if (!checked) {
      trackEvent('booking_blocked', { reason: 'privacy_policy_not_accepted' })
      toast.error('Debes aceptar la política de privacidad')
      return
    }

    if (address == null) {
      trackEvent('booking_blocked', { reason: 'address_missing' })
      toast.error('Debes seleccionar una dirección')
      return
    }

    if (selectedPharmacy == null) {
      trackEvent('booking_blocked', { reason: 'pharmacy_missing' })
      toast.error('Debes seleccionar una farmacia')
      return
    }

    void onSubmit()
  }

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (address == null) {
      setPharmacies([])
      return
    }

    async function fetchPharmacies (): Promise<void> {
      try {
        setFetchingPharmacies(true)

        const coordinates = {
          lat: address?.geometry?.location?.lat() as number,
          lng: address?.geometry?.location?.lng() as number
        }

        if (coordinates.lat == null || coordinates.lng == null) {
          trackEvent('pharmacies_search_error', { reason: 'missing_coordinates' })
          toast.error('Error al obtener coordenadas')
          return
        }

        trackEvent('pharmacies_search', { source: 'address_change' })

        const response = await getPrescriptionById({
          prescriptionId,
          coordinates,
          address: address?.formatted_address,
          addressDetails: placeToAddressDetails(address)
        })

        const counts = {
          total: response.pharmacies.length,
          internal: response.pharmacies.filter((p) => !p.pharmacy.isExternal).length,
          external: response.pharmacies.filter((p) => p.pharmacy.isExternal).length
        }

        trackEvent('pharmacies_found', {
          pharmacies_count: counts.total,
          internal_pharmacies: counts.internal,
          external_pharmacies: counts.external
        })

        trackPharmacyAvailability(counts, 'address_change')

        setPharmacies(response.pharmacies)
      } catch (error: any) {
        trackEvent('pharmacies_search_error', {
          reason: 'request_failed',
          error_message: error.response?.data?.message ?? error.message,
          error_status: error.response?.status
        })

        toast.error('Error al obtener farmacias')
      } finally {
        setFetchingPharmacies(false)
      }
    }

    void fetchPharmacies()
  }, [prescriptionId, address])

  useEffect(() => {
    if (address == null) {
      setSelectedPharmacy(null)
      return
    }

    // Try to get the already selected pharmacy
    const alreadySelected = pharmacies.find((p) => p.selected)
    if (alreadySelected != null) {
      setSelectedPharmacy(alreadySelected)
    } else {
      // Select the first non-external pharmacy, or the first one if all are external
      const nonExternalPharmacy = pharmacies.find((p) => !p.pharmacy.isExternal)
      const selected = nonExternalPharmacy ?? pharmacies[0] ?? null
      setSelectedPharmacy(selected)
    }

    setChangeAddressOpen(false)
    setChangePharmacyOpen(false)
  }, [fetchingPharmacies, pharmacies, address])

  const hasPrefilledAddress = initialAddress != null

  useEffect(() => {
    if (hasPrefilledAddress) {
      trackEvent('address_prefilled', { source: 'backend' })
    }
  }, [hasPrefilledAddress])

  const selectedPharmacyId = selectedPharmacy?.pharmacy.id
  const selectedIsExternal = selectedPharmacy?.pharmacy.isExternal === true

  // El aviso de "sin informacion en tiempo real" bloquea la reserva: interesa
  // saber cuantas veces se llega ahi.
  useEffect(() => {
    if (selectedPharmacyId != null && selectedIsExternal) {
      trackEvent('external_pharmacy_notice_view', { pharmacy_id: selectedPharmacyId })
    }
  }, [selectedPharmacyId, selectedIsExternal])

  const isLoading = fetchingPharmacies || submiting

  return (
    <PrescriptionDetailsPageStyle>
      <div className='p-4 grid grid-cols-1'>
        <PharmacySelectorDialogComponent
          open={changePharmacyOpen}
          onClose={() => setChangePharmacyOpen(false)}
          pharmacies={pharmacies}
          onChange={onPharmacySelect}
          selectedPharmacy={selectedPharmacy}
          address={address}
        />
        <div className='location-content-wrapper col-span-1'>
          <div className='location-title'>
            <p>
              Quiero localizar todos los medicamentos prescritos en la
              farmacia más cercana a:
            </p>
          </div>
          <div className='location-form'>
            {address != null && (
              <div className='location-form-row'>
                <div className='fake-input'>
                  <p>{address.formatted_address}</p>
                </div>
              </div>
            )}
            <div className='location-form-row other-location'>
              {changeAddressOpen
                ? (
                  <>
                    <p>Cambiar ubicación</p>
                    <PlaceSelectorComponent
                      onSelect={onPlaceSelect}
                      disabled={submiting}
                    />
                  </>
                  )
                : (
                  <Button
                    onClick={() => {
                      trackEvent('address_change_open', { has_address: address != null })
                      setChangeAddressOpen(true)
                    }}
                    disabled={submiting || disabled || fetchingPharmacies}
                    variant='outline'
                    className='w-full'
                  >
                    Cambiar ubicación
                  </Button>
                  )}
            </div>
            {address != null && (
              <>
                {selectedPharmacy?.pharmacy != null && (
                  <div className='location-form-row'>
                    <div className='fake-input'>
                      <p>{selectedPharmacy.pharmacy.description}</p>
                    </div>
                  </div>
                )}
                <div className='location-form-row'>
                  {fetchingPharmacies && pharmacies.length === 0
                    ? (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <LoadingComponent />
                      </div>
                      )
                    : (
                      <Button
                        onClick={() => {
                          trackEvent('pharmacy_selector_open', {
                            pharmacies_count: pharmacies.length,
                            selected_pharmacy_id: selectedPharmacy?.pharmacy.id
                          })
                          setChangePharmacyOpen(true)
                        }}
                        disabled={submiting || disabled || fetchingPharmacies || pharmacies.length === 0}
                        variant='outline'
                        className='w-full'
                      >
                        {pharmacies.length === 0 ? 'No hay farmacias disponibles' : 'Cambiar farmacia'}
                      </Button>
                      )}
                </div>
              </>
            )}
          </div>

        </div>
      </div>
      <div className='grid grid-cols-1 '>
        {address != null && selectedPharmacy != null && (
          selectedPharmacy.pharmacy.isExternal
            ? (
              <div className='external-pharmacy'>
                <p>Sobre esta farmacia no tenemos información en tiempo real. Desplazate a la dirección indicada y consulte al farmaceutico.</p>
                <p>Alternativamente puede seleccionar una farmacia en color verde.</p>
              </div>
              )
            : (
              <div className='confirm-row-container'>
                <div className='legal-row-container'>
                  <div className='legal-row-item'>
                    <div className='location-checkbox-container'>
                      <div className='location-checkbox-item'>
                        <Checkbox
                          id='terms1'
                          disabled={submiting || disabled}
                          onCheckedChange={val => {
                            trackEvent('privacy_policy_toggle', { accepted: Boolean(val) })
                            setChecked(Boolean(val))
                          }}
                          checked={checked}
                        />
                      </div>
                      <div className='location-checkbox-item'>
                        <label htmlFor='terms1'>
                          He leído y acepto la{' '}
                          <a href='https://ludapartners.com/politica-privacidad/' target='_blank' rel='noreferrer'>Política de Privacidad</a>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className='legal-row-item'>
                    <p>
                      Información básica sobre <a href='https://ludapartners.com/informacion-primera-capa/' target='_blank' rel='noreferrer'>protección de datos</a>
                    </p>
                  </div>
                </div>
                <ActionButton
                  onClick={() => handleOnSubmit()}
                  loading={isLoading}
                  disabled={selectedPharmacy.pharmacy.isExternal}
                >
                  Encargar YA
                </ActionButton>
              </div>
              )
        )}
      </div>
    </PrescriptionDetailsPageStyle>
  )
}
